import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Importing the copy icon
import { useNavigate } from 'react-router';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';

const EmbedChatbot = () => {
  const [domain, setDomain] = useState('https://');
  const [embedCode, setEmbedCode] = useState('');
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const [selectedOrganisation, setSelectedOrganisation] = useState(null); // Track selected organisation
  const loggedInUser = useRecoilValue(userInformationAtom);

  const handleGenerateScript = () => {
    // Validate if domain starts with 'https://' and contains at least one dot.
    if (!domain || !domain.startsWith('https://') || !domain.includes('.')) {
      showSnackBar("Domain must start with 'https://' and contain at least one dot.", 'error');
      return;
    }

    // Remove the "https://" part from the domain for the script
    const cleanDomain = domain.replace(/^https?:\/\//, '');

    // Define the fixed scriptSrc and data-origin
    const scriptSrc = 'https://md-chat-bot-widget.s3.ap-south-1.amazonaws.com/chatbot-widget.js';
    const dataOrigin = 'http://localhost:8000/ask'; // Fixed data-origin value

    // Generate the embed script with the fixed values
    const code = `
<html>
  <body>
    <script src="${scriptSrc}" data-site="${cleanDomain}" data-origin="${dataOrigin}"></script>
  </body>
</html>
    `.trim();

    setEmbedCode(code);
  };

  const handleCopyToClipboard = () => {
    if (embedCode) {
      navigator.clipboard
        .writeText(embedCode)
        .then(() => {
          showSnackBar('Script copied to clipboard!', 'success');
        })
        .catch(() => {
          showSnackBar('Failed to copy script. Please try again.', 'error');
        });
    }
  };

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => navigate('/'),
    },
    { label: 'Tools', onClick: () => navigate('/r/tools') },
    { label: 'Embed Chatbot' },
  ];

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(e, item) => item?.onClick?.()}
      />

      <Grid container justifyContent="center" sx={{ py: 5 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Generate Embed Script for Your Website
              </Typography>

              <Box mt={2}>
                  {loggedInUser?.is_superuser && (
                <OrganisationDropdown
                  value={selectedOrganisation}
                  onChange={(value) => setSelectedOrganisation(value)} // Update selected organization on change
                />
                  )}
              </Box>
              <Box mt={2}>
                <TextField
                  fullWidth
                  label="Enter Your Domain"
                  placeholder="https://www.example.com"
                  value={domain}
                  size="small"
                  onChange={(e) => setDomain(e.target.value)}
                  margin="normal"
                />
              </Box>

              <Box mt={3}>
                <Button variant="contained" size="small" onClick={handleGenerateScript}>
                  Generate Script
                </Button>
              </Box>

              {embedCode && (
                <Box mt={4} sx={{ position: 'relative' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Embed this HTML into your website:
                  </Typography>
                  <Paper
                    elevation={2}
                    sx={{
                      whiteSpace: 'pre-wrap',
                      background: '#f1f1f1',
                      padding: 2,
                      borderRadius: 2,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      position: 'relative', // Allow positioning the icon within the container
                    }}
                  >
                    {embedCode}
                    <IconButton
                      color="primary"
                      onClick={handleCopyToClipboard}
                      sx={{
                        position: 'absolute', // Position it at the top right
                        top: 8,
                        right: 8,
                      }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmbedChatbot;
